import React from "react";
import { Link } from "react-router-dom";
import "../styles/pages/CartPage.css";
import { useCart } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import { formatCurrency } from "../utils/currency";
import { resolveProductImageUrl } from "../utils/productImageUrl";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import { toast } from "../utils/toast";

const WishlistPage: React.FC = () => {
  const { addToCart } = useCart();
  const { items, removeFromWishlist } = useWishlist();
  const [activeActionId, setActiveActionId] = React.useState<number | null>(null);

  const handleMoveToCart = async (productId: number) => {
    const item = items.find((wishlistItem) => wishlistItem.product.id === productId);
    if (!item) {
      return;
    }

    setActiveActionId(productId);
    try {
      await addToCart(item.product);
      await removeFromWishlist(productId);
    } catch {
      toast.error("Couldn't move this item right now.");
    } finally {
      setActiveActionId(null);
    }
  };

  const handleRemove = async (productId: number) => {
    setActiveActionId(productId);
    try {
      await removeFromWishlist(productId);
    } catch {
      toast.error("Couldn't remove this item right now.");
    } finally {
      setActiveActionId(null);
    }
  };

  if (!items.length) {
    return (
      <section className="shell section page-section wishlist-page">
        <MobilePageHeader title="Wishlist" />
        <div className="store-card empty-state">
          <h1>Your wishlist is empty</h1>
          <p>Save products here so you can revisit and compare them later.</p>
          <Link className="button" to="/products?discover=1&view=collection">
            Explore products
          </Link>
        </div>
      </section>
    );
  }

  const wishlistValue = items.reduce((total, item) => total + item.product.price, 0);
  const wishlistOriginalValue = items.reduce((total, item) => total + item.product.originalPrice, 0);
  const wishlistSavings = Math.max(0, wishlistOriginalValue - wishlistValue);

  return (
    <section className="shell section page-section wishlist-page">
      <MobilePageHeader title="Wishlist" />

      <div className="cart-layout">
        <div className="cart-items">
          {items.map((item) => (
            <article className="store-card cart-item" key={item.product.id}>
              <button
                type="button"
                className="cart-item__close"
                aria-label={`Remove ${item.product.name} from wishlist`}
                onClick={() => void handleRemove(item.product.id)}
                disabled={activeActionId === item.product.id}
              >
                ×
              </button>
              <div className="cart-item__image-wrap">
                <img src={resolveProductImageUrl(item.product.images[0])} alt={item.product.name} />
              </div>
              <div className="cart-item__content">
                <div className="cart-item__details">
                  <span className="cart-item__brand">{item.product.brand || "Brand"}</span>
                  <h3 className="cart-item__title">{item.product.name}</h3>
                </div>
                <div className="cart-item__pricing">
                  <div className="cart-item__price-row">
                    <strong className="cart-item__price">{formatCurrency(item.product.price)}</strong>
                    {item.product.originalPrice > item.product.price ? (
                      <>
                        <span className="cart-item__original-price">{formatCurrency(item.product.originalPrice)}</span>
                        <span className="cart-item__off">
                          {Math.round(
                            ((item.product.originalPrice - item.product.price) / item.product.originalPrice) * 100,
                          )}
                          % Off
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="cart-item__action-group wishlist-item__action-group">
                  <button
                    type="button"
                    className="cart-item__action cart-item__action--primary"
                    onClick={() => void handleMoveToCart(item.product.id)}
                    disabled={activeActionId === item.product.id}
                  >
                    Add to cart
                  </button>
                  <button
                    type="button"
                    className="cart-item__action cart-item__action--danger"
                    onClick={() => void handleRemove(item.product.id)}
                    disabled={activeActionId === item.product.id}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
        <aside className="store-card summary-card cart-summary-card wishlist-summary-card">
          <div className="cart-summary-card__header">
            <span className="eyebrow">Wishlist summary</span>
            <h2>Your saved products</h2>
          </div>
          <div>
            <span>Products</span>
            <strong>{items.length}</strong>
          </div>
          <div>
            <span>Current value</span>
            <strong>{formatCurrency(wishlistValue)}</strong>
          </div>
          <div>
            <span>You save</span>
            <strong>{formatCurrency(wishlistSavings)}</strong>
          </div>
          <div className="summary-total">
            <span>Wishlist total</span>
            <strong>{formatCurrency(wishlistValue)}</strong>
          </div>
          <div className="cart-summary-card__note">
            <span>Keep exploring</span>
            <small>Move a saved product to your cart whenever you are ready.</small>
          </div>
          <Link className="button wishlist-summary-card__button" to="/products?discover=1&view=collection">
            Explore products
          </Link>
        </aside>
      </div>
    </section>
  );
};

export default WishlistPage;
